import { QueryResult } from 'react-query';
import moment from 'moment-timezone';
import { callApi, Controller, useApi, UseApiOptions } from './useApi';
// eslint-disable-next-line import/no-cycle
import { addAuftragPosition, AuftragPosition, defaultAuftragPosition } from './auftragPosition';
// eslint-disable-next-line import/no-cycle
import { getKunde, Kunde } from './kunde';
// eslint-disable-next-line import/no-cycle
import { Zusatzadresse } from './zusatzadresse';
import { defaultSmDate } from '../utils/formatter';
import { AngebotPosition } from './angebotPosition';
// eslint-disable-next-line import/no-cycle
import { Angebot, updateAngebot } from './angebot';
// eslint-disable-next-line import/no-cycle
import { getArtikelDetail } from './artikelDetail';
import { getArtikelDetailKalkulationen } from './artikelDetailKalkulation';
import { ServicePosition } from './servicePosition';
// eslint-disable-next-line import/no-cycle
import { Service, updateService } from './service';

export interface Auftrag {
    kNr?: number;
    nr: string;
    kaNr: number;
    kaName: string;
    angebotNr: string;
    serviceNr: string;
    abrufZu: string;
    lieferAdrNr: number;
    lieferKNr: number;
    rechnungsAdrNr: number;
    rechnungsKNr: number;
    status: number;
    datum: string;
    datBis: string;
    rahmen: boolean;
    betreuer: number;
    telBetreuer: number;
    txt: string;
    txtB1: number;
    ara: number;
    eva: boolean;
    liefBed: number;
    liefZeit: number;
    liefDat: string;
    liefKw: number;
    zahlBed: number;
    fracht: number;
    tipp: number;
    roadPr: number;
    mwstCode: number;
    sNetto: number;
    sMwst: number;
    sBrutto: number;
    teilR: number;
    aktText: string;
    betrUm: boolean;
    zusatz: string;
    anlageDat?: string;
    anlageUhr?: number;
    anlageUser?: string;
    updDat?: string;
    updUhr?: number;
    updUser?: string;

    kunde?: Kunde;
    lieferKunde?: Kunde;
    lieferAdr?: Zusatzadresse;
    rechnungsKunde?: Kunde;
    rechnungsAdr?: Zusatzadresse;
}

export function getDefaultAuftrag(kNr?: number): Auftrag {
    const values: Auftrag = {
        kNr: undefined,
        nr: '',
        kaNr: 0,
        angebotNr: '',
        serviceNr: '',
        abrufZu: '',
        lieferAdrNr: 0,
        lieferKNr: 0,
        rechnungsAdrNr: 0,
        rechnungsKNr: 0,
        kaName: '',
        status: 0,
        datum: defaultSmDate,
        datBis: defaultSmDate,
        rahmen: false,
        betreuer: 0,
        telBetreuer: 0,
        txt: '',
        txtB1: 1,
        ara: 0,
        eva: false,
        liefBed: 17,
        liefDat: defaultSmDate,
        liefZeit: 0,
        liefKw: 0,
        zahlBed: 1,
        fracht: 0,
        tipp: 0,
        roadPr: 0,
        mwstCode: 0,
        sNetto: 0,
        sMwst: 0,
        sBrutto: 0,
        teilR: 0,
        aktText: '',
        betrUm: false,
        zusatz: '',
    };

    const now = moment();

    return {
        ...values,
        kNr,
        datum: now.format('YYYY-MM-DD') + 'T00:00:00',
        // datBis: now.clone().add({ days: 7 }).format('YYYY-MM-DD') + 'T00:00:00',
    };
}

export interface AuftragFilter {
    nr?: string;
    nrSearch?: string;
    auNrList?: string[];
    kNr?: number;
    konzernNr?: number;
    abrufZu?: string;
    abrufZuSearch?: string;
    abrufZuList?: string[];
    artikelGruppeNr?: number;
    artikelNr?: string;
    searchText?: string;
    betreuer?: number;
    betreuerExact?: number;
    telBetreuerExact?: number;
    datumVon?: string;
    datumBis?: string;
    kundePlzVon?: string;
    kundePlzBis?: string;
    kundeUmsatzVon?: number | null;
    kundeUmsatzBis?: number | null;
    kundeBranchen?: number[];
    kundeErweiterteSuchen?: number[];
    kundeInteresseFuerProdukte?: number[];
    kundeBetreuerExact?: number;
    kundeTelBetreuerExact?: number;
    produktgruppen?: number[];
    lArt?: string;
    status?: number;
    statusList?: number[];
    rahmen?: boolean;
    onlyOpen?: 'offen' | 'status';
    withKunde?: boolean;
    maxRows?: number;
    sortAsc?: boolean;
}

export function useAuftrag(filter: AuftragFilter, apiOptions?: UseApiOptions<Auftrag>): QueryResult<Auftrag> {
    return useApi<Auftrag>(Controller.auftrag + '/one', { filter }, apiOptions);
}

export function useAuftragList(filter: AuftragFilter, apiOptions?: UseApiOptions<Auftrag[]>): QueryResult<Auftrag[]> {
    return useApi<Auftrag[]>(Controller.auftrag + '/list', { filter }, apiOptions);
}

interface AddAuftragResult {
    newNr: string;
}

export async function addAuftrag(auftrag: Auftrag): Promise<AddAuftragResult> {
    return callApi<AddAuftragResult>(Controller.auftrag + '/add', { auftrag });
}

export async function updateAuftrag(auftrag: Auftrag): Promise<void> {
    return callApi<void>(Controller.auftrag + '/update', { auftrag });
}

export async function deleteAuftrag(auftrag: Auftrag): Promise<void> {
    return callApi<void>(Controller.auftrag + '/delete', { auftrag });
}

export async function sendAuftrag(auftrag: Auftrag, kaNrListTo: number[], kaNrListCC: number[]): Promise<void> {
    return callApi<void>(Controller.auftrag + '/send', { nr: auftrag.nr, kaNrListTo, kaNrListCC });
}

/*
export async function bookAuftrag(auftrag: Auftrag): Promise<void> {
    return callApi<void>(Controller.auftrag + '/book', { auftrag });
}
*/

export async function addAuftragFromAngebot(
    kunde: Kunde | null,
    angebot: Angebot,
    angebotPositionen?: AngebotPosition[],
): Promise<AddAuftragResult> {
    const defaultAuftrag = getDefaultAuftrag();

    const lieferAdr = kunde?.zusatzadresseList?.find((za) => za.typ === 'L' && za.std && !za.archiv);
    const rechnungsAdr = kunde?.zusatzadresseList?.find((za) => za.typ === 'R' && za.std && !za.archiv);

    const newAuftrag: Auftrag = {
        ...defaultAuftrag,
        kNr: angebot.kNr,
        kaNr: angebot.kaNr,
        angebotNr: angebot.nr,
        betreuer: angebot.betreuer,
        telBetreuer: angebot.telBetreuer,
        txt: angebot.txt,
        ara: angebot.ara,
        aktText: angebot.aktText,
        liefBed: angebot.liefBed,
        liefDat: angebot.liefDat,
        liefZeit: angebot.liefZeit,
        liefKw: angebot.liefKw,
        mwstCode: angebot.mwstCode,
        zusatz: angebot.zusatz,

        zahlBed: angebot.zahlBed,
        lieferAdrNr: lieferAdr?.nr || 0,
        lieferAdr,
        rechnungsAdrNr: rechnungsAdr?.nr || 0,
        rechnungsAdr,
    };

    const result = await addAuftrag(newAuftrag);

    if (angebotPositionen) {
        // eslint-disable-next-line no-restricted-syntax
        for (const angebotPosition of angebotPositionen) {
            if (angebotPosition.transfer) {
                // eslint-disable-next-line no-await-in-loop
                await addAuftragPosition({
                    ...defaultAuftragPosition,
                    kNr: angebot.kNr || defaultAuftragPosition.kNr,
                    auNr: result.newNr,
                    artikelGruppeNr: angebotPosition.artikelGruppeNr,
                    artikelNr: angebotPosition.artikelNr,
                    pos: angebotPosition.pos,
                    posText: angebotPosition.posText,
                    posEinh: angebotPosition.posEinh,
                    // posMenge: angebotPosition.posMenge,
                    posEkPreis: angebotPosition.posEkPreis,
                    posEPreis: angebotPosition.posEPreis,
                    posNetto: angebotPosition.posNetto,
                    posMwst: angebotPosition.posMwst,
                    posBrutto: angebotPosition.posBrutto,
                    posBem: angebotPosition.posBem,
                    // offen: angebotPosition.posMenge,

                    posMenge: angebotPosition.transferMenge || 0,
                    offen: angebotPosition.transferMenge || 0,
                    lArt: angebotPosition.transferLArt || '',
                });
            }
        }
    }

    await updateAngebot({
        ...angebot,
        status: 2,
        auNr: result.newNr,
    });

    return result;
}

export async function addAuftragFromService(
    kunde: Kunde | null,
    service: Service,
    servicePositionen?: ServicePosition[],
): Promise<AddAuftragResult> {
    const defaultAuftrag = getDefaultAuftrag();

    let lieferAdr = kunde?.zusatzadresseList?.find((za) => za.typ === 'L' && za.std && !za.archiv);
    const rechnungsAdr = kunde?.zusatzadresseList?.find((za) => za.typ === 'R' && za.std && !za.archiv);

    if (service.lieferAdrNr) {
        lieferAdr = kunde?.zusatzadresseList?.find((za) => za.typ === 'L' && za.nr === service.lieferAdrNr);
    }

    const newAuftrag: Auftrag = {
        ...defaultAuftrag,
        kNr: service.kNr,
        kaNr: service.kaNr,
        serviceNr: service.nr,
        betreuer: service.betreuer,
        telBetreuer: service.telBetreuer,
        txt: service.txt,
        ara: service.ara,
        aktText: service.aktText,
        mwstCode: service.mwstCode,
        zusatz: service.zusatz,
        liefBed: service.liefBed,
        liefDat: service.liefDat,
        liefZeit: service.liefZeit,
        liefKw: service.liefKw,
        zahlBed: service.zahlBed,

        lieferAdrNr: lieferAdr?.nr || 0,
        lieferAdr,
        rechnungsAdrNr: rechnungsAdr?.nr || 0,
        rechnungsAdr,
    };

    const result = await addAuftrag(newAuftrag);

    if (servicePositionen) {
        // eslint-disable-next-line no-restricted-syntax
        for (const servicePosition of servicePositionen) {
            if (servicePosition.transfer) {
                // eslint-disable-next-line no-await-in-loop
                await addAuftragPosition({
                    ...defaultAuftragPosition,
                    kNr: service.kNr || defaultAuftragPosition.kNr,
                    auNr: result.newNr,
                    artikelGruppeNr: servicePosition.artikelGruppeNr,
                    artikelNr: servicePosition.artikelNr,
                    pos: servicePosition.pos,
                    posText: servicePosition.posText,
                    posEinh: servicePosition.posEinh,
                    // posMenge: servicePosition.posMenge,
                    posEkPreis: servicePosition.posEkPreis,
                    posEPreis: servicePosition.posEPreis,
                    posNetto: servicePosition.posNetto,
                    posMwst: servicePosition.posMwst,
                    posBrutto: servicePosition.posBrutto,
                    posBem: servicePosition.posBem,
                    // offen: servicePosition.posMenge,

                    posMenge: servicePosition.transferMenge || 0,
                    offen: servicePosition.transferMenge || 0,
                    lArt: servicePosition.transferLArt || '',
                });
            }
        }
    }

    await updateService({
        ...service,
        status: 3,
        auNr: result.newNr,
    });

    return result;
}

export async function copyAuftrag(
    kNr: number,
    auftrag: Auftrag,
    auftragPositionen?: AuftragPosition[],
): Promise<AddAuftragResult> {
    const newKundeList = await getKunde({ nr: kNr, withZusatzadresseList: true });

    if (newKundeList.length !== 1) {
        throw new Error('Kunde nicht gefunden');
    }

    const newKunde = newKundeList[0];

    let lieferAdrNr = 0;
    let rechnungsAdrNr = 0;
    if (auftrag.lieferAdrNr && newKunde.nr === auftrag.kNr) {
        const lieferAdr = newKunde.zusatzadresseList?.find((za) => za.nr === auftrag.lieferAdrNr);
        if (lieferAdr && !lieferAdr.archiv) {
            lieferAdrNr = lieferAdr.nr;
        }
    }

    if (auftrag.rechnungsAdrNr && newKunde.nr === auftrag.kNr) {
        const rechnungsAdr = newKunde.zusatzadresseList?.find((za) => za.nr === auftrag.rechnungsAdrNr);
        if (rechnungsAdr && !rechnungsAdr.archiv) {
            rechnungsAdrNr = rechnungsAdr.nr;
        }
    }

    const defaultAuftrag = getDefaultAuftrag();

    const newAuftrag = {
        ...auftrag,
        kNr,
        status: defaultAuftrag.status,
        datum: defaultAuftrag.datum,
        datBis: defaultAuftrag.datBis,
        betreuer: newKunde.betreuer || defaultAuftrag.betreuer,
        telBetreuer: newKunde.telBetreuer || defaultAuftrag.telBetreuer,
        zahlBed: newKunde.zahlZiel || defaultAuftrag.zahlBed,
        lieferAdrNr,
        rechnungsAdrNr,
    };

    const result = await addAuftrag(newAuftrag);

    if (auftragPositionen) {
        // eslint-disable-next-line no-restricted-syntax
        for (const auftragPosition of auftragPositionen) {
            let { posText } = auftragPosition;

            let ekPreis = auftragPosition.posEkPreis;
            try {
                // eslint-disable-next-line no-await-in-loop
                const artikelDetail = await getArtikelDetail({
                    artikelGruppeNr: auftragPosition.artikelGruppeNr,
                    artikelNr: auftragPosition.artikelNr,
                    includeArchiv: true,
                });

                if (artikelDetail.artikel?.text) {
                    posText = artikelDetail.artikel.text;
                }

                // eslint-disable-next-line no-await-in-loop
                const artikelDetailKalkulationen = await getArtikelDetailKalkulationen({
                    artikelGruppeNr: auftragPosition.artikelGruppeNr,
                    artikelNr: auftragPosition.artikelNr,
                });

                if (artikelDetail && artikelDetailKalkulationen) {
                    const defaultKalkulation = artikelDetailKalkulationen.find(
                        (k) => k.eh === artikelDetail.artikel?.einheit,
                    );

                    if (defaultKalkulation?.ek2) {
                        ekPreis = defaultKalkulation.ek2;
                    }
                }
            } catch (error) {
                console.error(error);
            }

            // eslint-disable-next-line no-await-in-loop
            await addAuftragPosition({
                ...auftragPosition,
                posText,
                offen: auftragPosition.posMenge,
                auNr: result.newNr,
                posEkPreis: ekPreis,
            });
        }
    }

    return result;
}

export async function addAuftragFromAuftrag(
    auftrag: Auftrag,
    auftragPositionen?: AuftragPosition[],
): Promise<AddAuftragResult> {
    const defaultAuftrag = getDefaultAuftrag();

    const newAuftrag: Auftrag = {
        ...auftrag,
        status: defaultAuftrag.status,
        datum: defaultAuftrag.datum,
        rahmen: false,
        abrufZu: auftrag.nr,
    };

    const result = await addAuftrag(newAuftrag);
    newAuftrag.nr = result.newNr;

    if (auftragPositionen) {
        let pos = 1;
        // eslint-disable-next-line no-restricted-syntax
        for (const auftragPosition of auftragPositionen) {
            if (auftragPosition.transfer) {
                // eslint-disable-next-line no-await-in-loop
                await addAuftragPosition({
                    ...auftragPosition,
                    auNr: result.newNr,
                    pos,
                    posMenge: auftragPosition.transferMenge || 0,
                    offen: auftragPosition.transferMenge || 0,
                    lArt: auftragPosition.transferLArt || '',
                });
                pos += 1;
            }
        }
    }

    // await bookAuftrag(newAuftrag);

    return result;
}

interface RecalculateAuftragResult {
    auftrag: Auftrag;
}

export async function recalculateAuftrag(auftrag: Auftrag): Promise<RecalculateAuftragResult> {
    return callApi<RecalculateAuftragResult>(Controller.auftrag + '/recalculate', { auftrag });
}

export async function finalizeAuftrag(auftrag: Auftrag, setOffenToZero: boolean): Promise<RecalculateAuftragResult> {
    return callApi<RecalculateAuftragResult>(Controller.auftrag + '/finalize', { auftrag, setOffenToZero });
}
